
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import AMapLoader from '@amap/amap-jsapi-loader'
import { MapSearchRadius } from '@/utils/coinfig'
import { getCurrentPosition } from '@/utils/coord'
import { IsMobile as IsMobile } from '@/utils/check'

// 声明高德地图全局变量
declare global {
  interface Window {
    AMap: any
  }
}

// 定义位置信息接口
interface LocationInfo {
  position: {
    longitude: number
    latitude: number
  }
  address: string
}

@Component({
  name: 'LocationChooseDialog',
})
export default class LocationChooseDialog extends Vue {
  // 对话框标题
  @Prop({ default: '选择位置' }) private title!: string
  // 对话框是否可见
  @Prop({ default: false }) private value!: boolean
  // 初始位置
  @Prop() private initialPosition?: [number, number]
  // 默认放大级别
  @Prop({ default: 15 }) private defaultZoom!: number

  // 对话框可见状态（内部使用）
  private visible = false
  // 选择的位置信息
  private selectedLocation: LocationInfo | null = null
  // 地图中心点
  private center: [number, number] = [116.397428, 39.90923] // 默认北京
  // 当前缩放级别
  private zoom = 15
  // 是否正在定位
  private locating = false
  // 搜索关键词
  private searchKeyword = ''

  // 高德地图实例
  private map: any = null
  // 搜索服务
  private placeSearch: any = null
  // 地理编码服务
  private geocoder: any = null
  // 定位服务
  private geolocation: any = null
  // 当前选中标记点
  private currentMarker: any = null

  get isMobile() {
    return IsMobile()
  }

  @Watch('value')
  onValueChange(val: boolean) {
    this.visible = val
    if (val) {
      this.$nextTick(() => {
        // 确保DOM已更新再初始化地图
        this.initMap()
      })
    }
  }

  @Watch('visible')
  onVisibleChange(val: boolean) {
    this.$emit('input', val)
    if (!val && this.map) {
      // 关闭时销毁地图实例
      this.map.destroy()
      this.map = null
      this.currentMarker = null

      // 移除可能残留的遮罩层
      const modals = document.querySelectorAll('.v-modal')
      modals.forEach(modal => {
        if (modal.parentNode) {
          modal.parentNode.removeChild(modal)
        }
      })
    }
  }

  mounted() {
    this.visible = this.value
    this.zoom = this.defaultZoom

    if (this.initialPosition) {
      this.center = this.initialPosition
    }

    if (this.visible) {
      this.$nextTick(() => {
        this.initMap()
      })
    }
  }

  beforeDestroy() {
    // 销毁地图实例，释放资源
    if (this.map) {
      this.map.destroy()
    }
  }

  /**
   * 初始化高德地图
   */
  private initMap() {
    if (this.map) return // 已初始化过，不重复初始化


    // 加载高德地图
    AMapLoader.load({
      key: process.env.VUE_APP_AMAP_KEY || "", // Web端开发者Key
      version: "2.0", // JSAPI版本
      plugins: ["AMap.Scale", "AMap.ToolBar", "AMap.Geolocation", "AMap.Geocoder", "AMap.PlaceSearch"], // 需要使用的插件
    }).then((AMap) => {
      console.log("高德地图加载成功")

      // 创建地图实例
      this.map = new AMap.Map("location-map-container", {
        zoom: this.zoom,
        center: this.center,
        viewMode: '2D',
        resizeEnable: true,
        mapStyle: 'amap://styles/normal',
        zooms: [3, 20],
        defaultCursor: 'default',
        jogEnable: false,
        pitchEnable: false,
        rotateEnable: false
      })

      // 添加地图控件
      this.map.addControl(new AMap.Scale({
        position: 'LB'
      }))

      this.map.addControl(new AMap.ToolBar({
        position: 'RT',
        offset: new AMap.Pixel(10, 10)
      }))

      // 初始化地理编码服务
      this.geocoder = new AMap.Geocoder({
        radius: MapSearchRadius, // 查找范围
        extensions: 'all' // 返回基本+附加地址信息
      })

      // 初始化定位服务
      this.geolocation = new AMap.Geolocation({
        enableHighAccuracy: true, // 是否使用高精度定位
        timeout: 10000, // 超时时间
        zoomToAccuracy: true, // 定位成功后调整地图视野
        buttonPosition: 'RT', // 显示的位置
        buttonOffset: new AMap.Pixel(10, 50),
        showButton: true, // 显示默认的定位按钮
        panToLocation: true, // 自动移动到定位位置
        maximumAge: 0 // 不缓存定位结果
      })

      // 将定位控件添加到地图
      this.map.addControl(this.geolocation)

      // 初始化搜索服务
      this.placeSearch = new AMap.PlaceSearch({
        pageSize: 10,
        pageIndex: 1,
        city: '全国',
        map: this.map,
        panel: false,
        autoFitView: true
      })

      // 添加地图点击事件
      this.map.on('click', (e: any) => {
        const position: [number, number] = [e.lnglat.lng, e.lnglat.lat]
        this.handleMapClick(position)
      })

      // 地图加载完成后，设置初始标记点
      this.map.on('complete', () => {
        console.log('地图加载完成')
        if (this.initialPosition) {
          this.map.setCenter(this.initialPosition)
          this.updateMarkerWithPosition(this.initialPosition)
        } else {
          this.locateCurrentPosition()
        }
      })

    }).catch(e => {
      console.error('高德地图加载失败', e)
      this.$message.error('地图加载失败，请刷新重试')
    })
  }

  /**
   * 处理地图点击事件
   */
  private handleMapClick(position: [number, number]) {
    // 更新标记点位置
    this.updateMarkerWithPosition(position)
  }

  /**
   * 处理搜索请求
   */
  private handleSearch() {
    if (!this.placeSearch || !this.searchKeyword.trim()) return

    // 执行关键字搜索
    this.placeSearch.search(this.searchKeyword, (status: string, result: any) => {
      if (status === 'complete' && result.info === 'OK') {
        if (result.poiList.pois && result.poiList.pois.length > 0) {
          // 获取第一个结果
          const firstPoi = result.poiList.pois[0]
          const position: [number, number] = [firstPoi.location.lng, firstPoi.location.lat]

          // 更新地图中心点和标记
          this.map.setCenter(position)
          this.updateMarkerWithPosition(position, firstPoi.name)
        }
      } else {
        this.$message.warning('未找到相关地点')
      }
    })
  }

  /**
   * 定位到当前位置
   */
  private locateCurrentPosition() {
    if (!this.geolocation) return

    this.locating = true

    let successCallback = (result: any) => {
      this.locating = false
      const position: [number, number] = [result.position.lng, result.position.lat]
      this.map.setCenter(position)
      this.getLocationName(position).then(address => {
        this.updateMarkerWithPosition(position, address)
      })
    }

    let errorCallback = (result: any) => {
      this.locating = false
      this.$message.error('获取位置失败：' + result.message)
    }

    getCurrentPosition(this, this.geolocation, successCallback, errorCallback)


    // this.geolocation.getCurrentPosition((status: string, result: any) => {
    //   this.locating = false

    //   if (status === 'complete') {
    //     const position: [number, number] = [result.position.lng, result.position.lat]

    //     // 更新地图中心点
    //     this.map.setCenter(position)

    //     // 获取地址信息并更新标记点
    //     this.getLocationName(position).then(address => {
    //       this.updateMarkerWithPosition(position, address)
    //     })

    //   } else {
    //     // 定位失败
    //     this.$message.error('获取位置失败：' + result.message)
    //   }
    // })
  }

  /**
   * 获取位置的地点名称
   */
  private getLocationName(position: [number, number]): Promise<string> {
    return new Promise((resolve) => {
      if (!this.geocoder) {
        resolve('未知地点')
        return
      }

      this.geocoder.getAddress(position, (status: string, result: any) => {
        if (status === 'complete' && result.info === 'OK') {
          // 获取成功，返回地址信息
          const address = result.regeocode.formattedAddress
          resolve(address)
        } else {
          // 获取失败，返回未知地点
          resolve('未知地点')
        }
      })
    })
  }

  /**
   * 更新标记点
   */
  private updateMarkerWithPosition(position: [number, number], address?: string) {
    if (!this.map) return

    // 如果没有提供地址，尝试获取地址
    if (!address) {
      this.getLocationName(position).then(newAddress => {
        this.createMarker(position, newAddress)
      })
    } else {
      this.createMarker(position, address)
    }
  }

  /**
   * 创建标记点
   */
  private createMarker(position: [number, number], address: string) {
    if (!this.map) return

    // 更新选中的位置信息
    this.selectedLocation = {
      position: { longitude: position[0], latitude: position[1] },
      address
    }

    // 如果已有标记点，先移除
    if (this.currentMarker) {
      this.map.remove(this.currentMarker)
    }

    // 创建新标记点
    this.currentMarker = new window.AMap.Marker({
      map: this.map,
      position: position,
      title: address,
      anchor: 'center',
      offset: new window.AMap.Pixel(0, 0),
      zIndex: 100
    })

    // 添加点击事件
    this.currentMarker.on('click', () => {
      // 点击标记点时不做特殊处理，因为位置信息已经更新
    })
  }

  /**
   * 放大地图
   */
  private zoomIn() {
    if (!this.map) return

    // 获取当前缩放级别并增加1
    const currentZoom = this.map.getZoom()
    this.map.setZoom(currentZoom + 1)
  }

  /**
   * 缩小地图
   */
  private zoomOut() {
    if (!this.map) return

    // 获取当前缩放级别并减少1
    const currentZoom = this.map.getZoom()
    this.map.setZoom(currentZoom - 1)
  }

  /**
   * 处理确认按钮点击事件
   */
  private handleConfirm() {
    if (this.selectedLocation) {
      this.$emit('confirm', this.selectedLocation)
      this.visible = false
    }
  }

  /**
   * 处理对话框关闭事件
   */
  private handleClosed() {
    // 重置状态
    this.searchKeyword = ''

    // 销毁地图实例，释放资源
    if (this.map) {
      this.map.destroy()
    }

    // 重置标记点和选中位置
    this.currentMarker = null

    if (!this.initialPosition) {
      this.selectedLocation = null
    }

    // 移除可能残留的遮罩层
    setTimeout(() => {
      const modals = document.querySelectorAll('.v-modal')
      if (modals.length > 1) { // 保留一个主对话框的遮罩
        for (let i = 1; i < modals.length; i++) {
          const modal = modals[i]
          if (modal && modal.parentNode) {
            modal.parentNode.removeChild(modal)
          }
        }
      }
    }, 100)
  }
}
